/**
 * Secure Key Storage Module
 *
 * This module handles the secure storage of encryption keys in the browser.
 * Keys are stored in IndexedDB for persistence, but the user's private key
 * is kept in memory only during the session for added security.
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import {
  decryptPrivateKey,
} from './keys';

// Database schema
interface MyTotTotDB extends DBSchema {
  'baby-keys': {
    key: string; // babyId
    value: {
      babyId: string;
      encryptedKey: string; // AES key encrypted with user's public key
    };
  };
  'user-config': {
    key: string;
    value: {
      key: string;
      value: string;
    };
  };
  'pending-events': {
    key: string;
    value: {
      id: string;
      babyId: string;
      type: string;
      timestamp: string;
      encryptedData: string;
      encryptedNotes?: string;
      createdAt: number;
    };
  };
}

const DB_NAME = 'mytottot-crypto';
const DB_VERSION = 2;

let db: IDBPDatabase<MyTotTotDB> | null = null;

/**
 * Initialize the IndexedDB database
 */
export async function initCryptoStorage(): Promise<void> {
  db = await openDB<MyTotTotDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Store for baby encryption keys
      if (!db.objectStoreNames.contains('baby-keys')) {
        db.createObjectStore('baby-keys', { keyPath: 'babyId' });
      }

      // Store for user configuration
      if (!db.objectStoreNames.contains('user-config')) {
        db.createObjectStore('user-config', { keyPath: 'key' });
      }

      // Store for events queued while offline
      if (!db.objectStoreNames.contains('pending-events')) {
        db.createObjectStore('pending-events', { keyPath: 'id' });
      }
    },
  });
}

/**
 * Get the database instance, initializing if necessary
 */
async function getDB(): Promise<IDBPDatabase<MyTotTotDB>> {
  if (!db) {
    await initCryptoStorage();
  }
  return db!;
}

// ============================================
// BABY KEY STORAGE
// ============================================

/**
 * Store a baby's encrypted key
 */
export async function storeBabyKey(
  babyId: string,
  encryptedKey: string
): Promise<void> {
  const database = await getDB();
  await database.put('baby-keys', { babyId, encryptedKey });
}

/**
 * Get a baby's encrypted key
 */
export async function getBabyEncryptedKey(babyId: string): Promise<string | null> {
  const database = await getDB();
  const entry = await database.get('baby-keys', babyId);
  return entry?.encryptedKey ?? null;
}

/**
 * Remove a baby's key
 */
export async function removeBabyKey(babyId: string): Promise<void> {
  const database = await getDB();
  await database.delete('baby-keys', babyId);
}

/**
 * Get all baby keys
 */
export async function getAllBabyKeys(): Promise<Map<string, string>> {
  const database = await getDB();
  const entries = await database.getAll('baby-keys');
  const map = new Map<string, string>();
  for (const entry of entries) {
    map.set(entry.babyId, entry.encryptedKey);
  }
  return map;
}

/**
 * Clear all baby keys
 */
export async function clearAllBabyKeys(): Promise<void> {
  const database = await getDB();
  await database.clear('baby-keys');
}

// ============================================
// USER CONFIG STORAGE
// ============================================

/**
 * Store a user configuration value
 */
export async function setUserConfig(key: string, value: string): Promise<void> {
  const database = await getDB();
  await database.put('user-config', { key, value });
}

/**
 * Get a user configuration value
 */
export async function getUserConfig(key: string): Promise<string | null> {
  const database = await getDB();
  const entry = await database.get('user-config', key);
  return entry?.value ?? null;
}

/**
 * Remove a user configuration value
 */
export async function removeUserConfig(key: string): Promise<void> {
  const database = await getDB();
  await database.delete('user-config', key);
}

/**
 * Clear all user configuration
 */
export async function clearUserConfig(): Promise<void> {
  const database = await getDB();
  await database.clear('user-config');
}

// ============================================
// SESSION KEY MANAGEMENT
// ============================================

// In-memory storage for session-only keys
// These are never persisted to IndexedDB for security
class SessionKeyManager {
  private privateKey: CryptoKey | null = null;
  private decryptedBabyKeys: Map<string, CryptoKey> = new Map();

  /**
   * Set the user's private key (in memory only)
   */
  setPrivateKey(key: CryptoKey): void {
    this.privateKey = key;
  }

  /**
   * Get the user's private key
   */
  getPrivateKey(): CryptoKey | null {
    return this.privateKey;
  }

  /**
   * Check if private key is loaded
   */
  hasPrivateKey(): boolean {
    return this.privateKey !== null;
  }

  /**
   * Store a decrypted baby key in memory
   */
  setBabyKey(babyId: string, key: CryptoKey): void {
    this.decryptedBabyKeys.set(babyId, key);
  }

  /**
   * Get a decrypted baby key from memory
   */
  getBabyKey(babyId: string): CryptoKey | null {
    return this.decryptedBabyKeys.get(babyId) ?? null;
  }

  /**
   * Check if baby key is loaded
   */
  hasBabyKey(babyId: string): boolean {
    return this.decryptedBabyKeys.has(babyId);
  }

  /**
   * Get all loaded baby keys
   */
  getAllBabyKeys(): Map<string, CryptoKey> {
    return new Map(this.decryptedBabyKeys);
  }

  /**
   * Clear a specific baby key
   */
  clearBabyKey(babyId: string): void {
    this.decryptedBabyKeys.delete(babyId);
  }

  /**
   * Clear all baby keys from memory
   */
  clearAllBabyKeys(): void {
    this.decryptedBabyKeys.clear();
  }

  /**
   * Clear all session keys (logout)
   */
  clear(): void {
    this.privateKey = null;
    this.decryptedBabyKeys.clear();
  }
}

export const sessionKeys = new SessionKeyManager();

// ============================================
// HIGH-LEVEL KEY OPERATIONS
// ============================================

/**
 * Initialize user session with their encrypted private key
 */
export async function initializeUserSession(
  encryptedPrivateKey: string,
  password: string
): Promise<void> {
  // Decrypt the private key with the user's password
  const privateKey = await decryptPrivateKey(encryptedPrivateKey, password);
  sessionKeys.setPrivateKey(privateKey);
}

/**
 * Load and decrypt baby keys for the session
 */
export async function loadBabyKeysForSession(
  babyEncryptedKeys: Array<{ babyId: string; encryptedBabyKey: string }>
): Promise<void> {
  const privateKey = sessionKeys.getPrivateKey();
  if (!privateKey) {
    throw new Error('User session not initialized');
  }

  // Clear stale baby keys from IndexedDB and memory before loading fresh ones
  await clearAllBabyKeys();
  sessionKeys.clearAllBabyKeys();

  for (const { babyId, encryptedBabyKey } of babyEncryptedKeys) {
    try {
      // Decrypt and store in session memory
      const { decryptKeyWithPrivateKey } = await import('./keys');
      const aesKey = await decryptKeyWithPrivateKey(encryptedBabyKey, privateKey);
      sessionKeys.setBabyKey(babyId, aesKey);

      // Only persist to IndexedDB if decryption succeeded
      await storeBabyKey(babyId, encryptedBabyKey);
    } catch {
      // This baby's encrypted key doesn't match the current user's RSA private key.
      // This can happen if the user re-registered (new RSA keys) but old baby
      // records still exist, or if access was granted with a different key pair.
      // The baby will be skipped and won't appear in the app.
      console.warn(
        `Skipping baby ${babyId}: encrypted key does not match current private key. ` +
        `This baby may need to be re-shared or the access record may be stale.`
      );
    }
  }
}

/**
 * Get or load a baby's decryption key
 */
export async function getBabyKey(babyId: string): Promise<CryptoKey | null> {
  // First check session memory
  const sessionKey = sessionKeys.getBabyKey(babyId);
  if (sessionKey) {
    return sessionKey;
  }

  // Try to load from IndexedDB
  const privateKey = sessionKeys.getPrivateKey();
  if (!privateKey) {
    return null;
  }

  const encryptedKey = await getBabyEncryptedKey(babyId);
  if (!encryptedKey) {
    return null;
  }

  try {
    const { decryptKeyWithPrivateKey } = await import('./keys');
    const aesKey = await decryptKeyWithPrivateKey(encryptedKey, privateKey);
    sessionKeys.setBabyKey(babyId, aesKey);
    return aesKey;
  } catch {
    return null;
  }
}

/**
 * Clear all session data (logout)
 */
export async function clearSession(): Promise<void> {
  sessionKeys.clear();
  await clearAllBabyKeys();
  await clearUserConfig();
}

/**
 * Check if the crypto system is ready
 */
export function isCryptoReady(): boolean {
  return sessionKeys.hasPrivateKey();
}

// ============================================
// PENDING EVENTS (OFFLINE QUEUE)
// ============================================

export interface PendingEventRecord {
  id: string;
  babyId: string;
  type: string;
  timestamp: string;
  encryptedData: string;
  encryptedNotes?: string;
  createdAt: number;
}

export async function addPendingEvent(event: PendingEventRecord): Promise<void> {
  const database = await getDB();
  await database.put('pending-events', event);
}

export async function getAllPendingEvents(): Promise<PendingEventRecord[]> {
  const database = await getDB();
  return database.getAll('pending-events');
}

export async function removePendingEvent(id: string): Promise<void> {
  const database = await getDB();
  await database.delete('pending-events', id);
}
